import { useProposalForm } from '@/features/proposals/hooks/use-proposal-form.hook';
import { Text } from '@noorden-insurtech/alba-design-system';
import type { Dispatch, SetStateAction } from 'react';
import {
  PME_BILLING_CUT_OPTIONS,
  PME_BILLING_FREQUENCY_OPTIONS,
  PME_PAYMENT_METHOD_OPTIONS,
} from '../proposal.mock';
import type { PmeProposalStepProps } from '../proposal.types';
import { PmeStepFooter } from './sections/step-footer.component';

export type PmePaymentValues = {
  billingFrequency: string;
  paymentMethod: string;
  billingCut: string;
  dueDay: string;
};

export const pmePaymentDefaultValues: PmePaymentValues = {
  billingFrequency: 'mensal',
  paymentMethod: 'boleto',
  billingCut: '',
  dueDay: '',
};

type Props = PmeProposalStepProps & {
  values?: PmePaymentValues;
  onValuesChange?: Dispatch<SetStateAction<PmePaymentValues>>;
};

function isValidDueDay(value: string): boolean {
  const n = Number(String(value || '').replace(/\D/g, ''));
  return Number.isInteger(n) && n >= 1 && n <= 31;
}

export function StepPmePayment({
  onNext,
  onBack,
  canGoBack,
  canGoNext,
  saving,
  isViewMode = false,
  values,
  onValuesChange,
}: Props) {
  const form = useProposalForm({
    defaultValues: values ?? pmePaymentDefaultValues,
    onSubmit: async () => {},
  });

  const handleNext = async () => {
    if (isViewMode) {
      onNext();
      return;
    }
    const results = await Promise.all([
      form.validateField('billingCut', 'change'),
      form.validateField('dueDay', 'change'),
    ]);
    const hasErrors = results.some(r => Array.isArray(r) && r.length > 0);
    if (hasErrors) return;
    onNext({ ...form.state.values });
  };

  const handleBack = () => {
    onValuesChange?.({ ...form.state.values });
    onBack();
  };

  return (
    <div className='flex flex-col gap-10 [&_label]:font-bold [&_label]:text-alba-black-500'>
      <div className='flex flex-col gap-6'>
        <Text className='text-base font-normal text-alba-black-500'>
          Informações de Pagamento
        </Text>
        <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
          <form.AppField
            name='billingFrequency'
            children={field => (
              <field.SelectField
                label='Frequência de Cobrança'
                placeholder='Selecione a frequência'
                options={PME_BILLING_FREQUENCY_OPTIONS}
                required
                disabled
              />
            )}
          />
          <form.AppField
            name='paymentMethod'
            children={field => (
              <field.SelectField
                label='Meio de Pagamento'
                placeholder='Selecione o meio de pagamento'
                options={PME_PAYMENT_METHOD_OPTIONS}
                required
                disabled
              />
            )}
          />
        </div>
      </div>

      <div className='flex flex-col gap-6'>
        <Text className='text-base font-normal text-alba-black-500'>
          Faturamento
        </Text>
        <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
          <form.AppField
            name='billingCut'
            validators={{
              onChange: ({ value }: { value: unknown }) =>
                String(value ?? '').trim()
                  ? undefined
                  : 'Selecione o período de corte',
            }}
            children={field => (
              <field.SelectField
                label='Período corte'
                placeholder='Selecione o período'
                options={PME_BILLING_CUT_OPTIONS}
                required
                disabled={isViewMode}
              />
            )}
          />
          <form.AppField
            name='dueDay'
            validators={{
              onChange: ({ value }: { value: unknown }) => {
                const raw = String(value ?? '').trim();
                if (!raw) return 'Informe o dia de vencimento';
                if (!isValidDueDay(raw)) return 'Informe um dia entre 1 e 31';
                return undefined;
              },
            }}
            children={field => (
              <field.TextField
                label='Dia de vencimento'
                placeholder='Digite o dia'
                maxLength={2}
                required
                disabled={isViewMode}
              />
            )}
          />
        </div>
      </div>

      <PmeStepFooter
        onBack={handleBack}
        canGoBack={canGoBack}
        onNext={handleNext}
        canGoNext={canGoNext && !saving}
      />
    </div>
  );
}
